import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Camera, Trash2, UserRound } from "lucide-react";

const inputCls = "w-full bg-black border border-white/15 rounded px-3 py-3 text-sm text-[#F5F2EB] placeholder-[#52525B] focus:outline-none focus:border-[#D4AF37]";
const labelCls = "block text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA] mb-1.5";

/** Driver app settings: profile photo (shown to passengers on the tracking
 *  page) + mobile phone. Name is locked — it's tied to payroll/1099 docs. */
export const DriverSettings = ({ onClose }) => {
  const { user, refresh } = useAuth();
  const fileRef = useRef(null);
  const [phone, setPhone] = useState(user?.phone || "");
  const [busy, setBusy] = useState(false);

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("photo", f);
      await api.post("/users/me/photo", fd);
      await refresh?.();
      toast.success("Photo updated — passengers will see it on their tracking page");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upload photo");
    } finally { setBusy(false); e.target.value = ""; }
  };

  const removePhoto = async () => {
    setBusy(true);
    try {
      await api.delete("/users/me/photo");
      await refresh?.();
      toast.success("Photo removed");
    } catch { toast.error("Could not remove photo"); }
    finally { setBusy(false); }
  };

  const savePhone = async () => {
    setBusy(true);
    try {
      await api.patch("/users/me", { phone });
      await refresh?.();
      toast.success("Phone updated — dispatch sees the new number");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <div className="mx-5 mt-4 border border-[#D4AF37]/30 bg-[#0A0A0A] p-5" data-testid="driver-settings">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-serif text-xl">My settings</h2>
        <button data-testid="driver-settings-close" onClick={onClose} className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] border border-white/15 px-3 py-1.5">Close</button>
      </div>

      <div className={labelCls}>Profile photo — passengers see this while tracking their ride</div>
      <div className="flex items-center gap-4 mb-6">
        {user?.picture ? (
          <img src={user.picture} alt="" className="w-16 h-16 rounded-full object-cover border border-[#D4AF37]/40" data-testid="driver-settings-avatar"/>
        ) : (
          <div className="w-16 h-16 rounded-full bg-white/5 border border-white/15 flex items-center justify-center">
            <UserRound size={24} className="text-[#71717a]"/>
          </div>
        )}
        <div className="flex gap-2">
          <button data-testid="driver-photo-upload-btn" disabled={busy} onClick={() => fileRef.current?.click()}
            className="flex items-center gap-1.5 border border-white/20 px-4 py-2 text-[10px] uppercase tracking-[0.16em] text-[#F5F2EB] hover:border-[#D4AF37] hover:text-[#D4AF37] disabled:opacity-50">
            <Camera size={13}/> {user?.picture ? "Change" : "Add photo"}
          </button>
          {user?.picture && (
            <button data-testid="driver-photo-remove-btn" disabled={busy} onClick={removePhoto}
              className="flex items-center gap-1.5 border border-red-500/40 px-4 py-2 text-[10px] uppercase tracking-[0.16em] text-red-400 hover:bg-red-500/10 disabled:opacity-50">
              <Trash2 size={13}/> Remove
            </button>
          )}
        </div>
        <input ref={fileRef} data-testid="driver-photo-file-input" type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" className="hidden" onChange={uploadPhoto}/>
      </div>

      <div className={labelCls}>Mobile phone</div>
      <div className="flex gap-2 mb-3">
        <input data-testid="driver-settings-phone-input" type="tel" className={inputCls} value={phone} onChange={e => setPhone(e.target.value)} placeholder="(414) 555-0123"/>
        <button data-testid="driver-settings-save-phone" disabled={busy || phone === (user?.phone || "")} onClick={savePhone}
          className="shrink-0 btn-gold !px-5 text-[10px] disabled:opacity-40">Save</button>
      </div>
      <p className="text-[10px] text-[#71717a]">Name changes are tied to your payroll documents — call dispatch at (414) 333-6275 to update it.</p>
    </div>
  );
};
